import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { Link } from "wouter";

import { useI18n } from "@/hooks/use-i18n";
import { useLocalizedUrl } from "@/hooks/use-localized-url";

type Stato = "idle" | "loading" | "checkInbox" | "done" | "invalidEmail" | "error";

export function NewsletterForm() {
  const { t, language } = useI18n();
  const getLocalizedUrl = useLocalizedUrl();
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [stato, setStato] = useState<Stato>("idle");

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setStato("loading");
    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, language, website }),
      });
      if (res.status === 400) return setStato("invalidEmail");
      if (!res.ok) return setStato("error");
      const data = (await res.json()) as { status: string };
      setStato(data.status === "confirmed" ? "done" : "checkInbox");
    } catch {
      setStato("error");
    }
  }

  const messaggio =
    stato === "checkInbox" ? t("footer.newsletterCheckInbox")
    : stato === "done" ? t("footer.newsletterDone")
    : stato === "invalidEmail" ? t("footer.newsletterInvalidEmail")
    : stato === "error" ? t("footer.newsletterError")
    : "";

  const isChiuso = stato === "checkInbox" || stato === "done";

  return (
    <div className="relative">
      <h3 className="text-base font-semibold leading-snug text-white/[.94]">{t("footer.newsletterTitle")}</h3>
      <p className="mt-3 text-sm text-white/[.68]">{t("footer.newsletterClaim")}</p>

      {!isChiuso && (
        <form onSubmit={handleSubmit} className="mt-3">
          <label htmlFor="newsletter-email" className="sr-only">{t("footer.emailPlaceholder")}</label>
          {/* Campo chiaro su scuro, pulsante tondo dentro il campo: stessa altezza (44) dei controlli standard */}
          <div className="flex h-11 max-w-[360px] items-center gap-2 rounded-control border border-white/[.16] bg-white/[.05] pl-3.5 pr-[5px] transition-colors focus-within:border-white/[.32]">
            <input
              id="newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("footer.emailPlaceholder")}
              className="h-full min-w-0 flex-1 bg-transparent text-sm text-white/[.94] placeholder:text-white/[.46] focus:outline-none"
            />
            <button
              type="submit"
              aria-label={t("footer.subscribe")}
              disabled={stato === "loading"}
              className="relative grid h-[34px] w-[34px] shrink-0 after:absolute after:-inset-[5px] after:content-[''] place-items-center rounded-full bg-action text-white transition-colors hover:bg-action-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:opacity-70"
            >
              {stato === "loading" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
          </div>
          {/* Honeypot: invisibile agli umani, i bot lo compilano */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="absolute left-[-9999px] h-px w-px"
          />
        </form>
      )}

      {/* Sempre nel DOM (una regione live che compare insieme al messaggio non viene annunciata); vuota non occupa spazio */}
      <p aria-live="polite" className="mt-3 text-sm text-white/[.86] empty:mt-0">
        {messaggio}
      </p>

      {!isChiuso && (
        <p className="mt-3 text-[13px] text-white/[.46]">
          <Link href={getLocalizedUrl("/privacy")} className="underline decoration-white/30 underline-offset-2 transition-colors hover:text-white/[.86]">
            {t("footer.newsletterPrivacy")}
          </Link>
        </p>
      )}
    </div>
  );
}
